import * as React from "react";
import {
  generatePath,
  matchPath,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";

import { type TriggerRecord } from "@/shared/domain/triggers";
import { useTrigger } from "@/shared/domain/triggers/hooks";
import { triggerIdFromSegment } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import { type TriggerRouteContext } from "./useTriggerRouteContext";

/**
 * The parent element of the `/triggers/:trigger` route
 */
export function TriggerRoute() {
  const { trigger = "" } = useParams<{ trigger: string }>();
  const triggerId = triggerIdFromSegment(trigger);
  const { data: record } = useTrigger(triggerId ?? undefined);

  useRegisterViewingContext("trigger", { orgId: record?.org_id });
  useTriggerNameInUrl(trigger, record);

  if (triggerId === null) {
    // The loader has already sent a segment with no trigger id to the list.
    return null;
  }
  return <Outlet context={{ triggerId } satisfies TriggerRouteContext} />;
}

/**
 * Callers link to a trigger by its bare id. Once `record` is read, this makes
 * `segment` (the URL's `:trigger`) read `<name>-<id>` with the trigger's
 * current name, for whoever reads the link: it adds the name to a bare id, and
 * replaces a name the trigger no longer has. The page, query string and hash
 * are kept. The id alone decides which trigger opens.
 */
function useTriggerNameInUrl(
  segment: string,
  record: TriggerRecord | undefined,
) {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    // `segment` is decoded, as React Router hands over params, so it is
    // compared with the plain name.
    if (
      record === undefined ||
      segment === `${record.name}-${record.trigger_id}`
    ) {
      return;
    }

    const pathname = insertTriggerNameInPath(location.pathname, record);
    const state: unknown = location.state;

    // Replace, so Back skips the URL it corrected.
    void navigate(
      { pathname, search: location.search, hash: location.hash },
      { replace: true, preventScrollReset: true, state },
    );
  }, [record, segment, location, navigate]);
}

/**
 * Puts the trigger's current name into the path's trigger segment:
 * /triggers/tr_x/edit -> /triggers/trigger-name-tr_x/edit, and
 * /triggers/old-name-tr_x/edit -> /triggers/trigger-name-tr_x/edit
 */
function insertTriggerNameInPath(
  pathname: string,
  record: TriggerRecord,
): string {
  // A React Router path pattern: `:trigger` names the trigger segment, and
  // `*` stands for the rest of the path after it (`edit`, a tab, or nothing
  // on the details tab).
  const triggerPages = "/triggers/:trigger/*";

  // `matchPath` reads the path against the pattern and returns the parts it
  // matched: "/triggers/tr_x/edit" gives `{ trigger: "tr_x", "*": "edit" }`.
  // The `*` part is the page to keep.
  const page = matchPath(triggerPages, pathname)?.params["*"] ?? "";

  // `generatePath` fills the same pattern back in, percent-encoding
  // `:trigger`.
  return generatePath(triggerPages, {
    trigger: `${record.name}-${record.trigger_id}`,
    "*": page,
  });
}
